import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Message } from '../types';
import dayjs from 'dayjs';
import CodeMirror from '@uiw/react-codemirror';
import { javascript } from '@codemirror/lang-javascript';
import { ClockIcon, ChevronDownIcon, ChevronUpIcon, DocumentIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { shortTime, cal_expire_in } from '../utils';
import ExpirationModal from './ExpirationModal';

interface MessageItemProps {
  message: Message;
  onUpdateExpiration: (messageId: string, expireIn: number) => void;
  onClickExpiration: (messageId: string) => void;
  currentSelectExpirationMessageId: string;
}

const MessageItem: React.FC<MessageItemProps> = ({ currentSelectExpirationMessageId, message, onUpdateExpiration, onClickExpiration }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // const expirationOptions = [
  //   { label: '2小时', value: 1 },
  //   { label: '4小时', value: 2 },
  //   { label: '1天', value: 3 },
  //   { label: '3天', value: 4 },
  //   { label: '7天', value: 5 },
  //   { label: '30天', value: 6 },
  // ];

  const copyIconRef = React.useRef<HTMLButtonElement>(null);

  // 在复制内容的函数中调用提示
  const copyContent = () => {
    navigator.clipboard.writeText(message.content);

    if (copyIconRef.current) {
      const _bg = copyIconRef.current.style.backgroundColor; // 改变背景色
      const _fg = copyIconRef.current.style.color; // 改变背景色
      copyIconRef.current.style.backgroundColor = 'green'; // 改变背景色
      copyIconRef.current.style.color = 'white'; // 改变背景色
      setTimeout(() => {
        if (copyIconRef.current) {
          copyIconRef.current.style.backgroundColor = _bg; // 还原背景色
          copyIconRef.current.style.color = _fg; // 改变背景色
        }
      }, 4000); // 延迟5秒
    }
  };

  const shouldCollapse = message.type === 'text' && message.content.length > 100;
  const displayContent = shouldCollapse && !isExpanded
    ? message.content.slice(0, 100) + '...'
    : message.content;

  const handleDoubleClick = () => {
    if (message.type === 'text' && message.content.length > 100) {
      setIsExpanded(!isExpanded);
    }
  };

  useEffect(() => {
    setIsModalOpen(currentSelectExpirationMessageId == message.id);
  }, [currentSelectExpirationMessageId])

  const handleClockClick = () => {
    if (currentSelectExpirationMessageId == message.id)
      //setIsModalOpen(false); // 打开模态窗口
      onClickExpiration('');
    else{
      onClickExpiration(message.id);
    }

  };

  const handleUpdateExpiration = async (expireIn: number) => {
    try {
      setIsUpdating(true);
      await onUpdateExpiration(message.id, expireIn);
      message.expire_at += cal_expire_in(expireIn);
    } finally {
      setIsUpdating(false);
      onClickExpiration('');
      //setIsModalOpen(false); // 关闭模态窗口
    }
  };


  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-2">
      <div className="flex justify-between text-sm text-gray-500">
        <span className="text-xxs">
          @{shortTime(message.created_at)}
          ({shortTime(message.expire_at)}过期)
        </span>
        <div className="flex items-center space-x-2">
          {/* <select
            onChange={(e) => handleUpdateExpiration(Number(e.target.value))}
            disabled={isUpdating}
            className={clsx(
              "text-sm border rounded px-2 py-1 transition-colors",
              isUpdating && "opacity-50 cursor-not-allowed"
            )}
          >
            <option value="">修改有效期</option>
            {expirationOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select> */}
          <button
            onClick={handleClockClick}
            className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            <ClockIcon className="w-4 h-4" />
          </button>

          {/* 复制图标 */}
          <button
            ref={copyIconRef}
            onClick={copyContent}
            className="px-2 py-1 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
      {isModalOpen && (
        <div id="selectBlock" className="bg-gray-100 shadow-lg py-2 w-full rounded-lg relative">
          <div className="absolute transform -translate-x-1/2 -top-2" style={{ right: '2.5rem' }}>
            <div className="w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-100"></div>
          </div>
          <div className="flex justify-center">
            <button className="m-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleUpdateExpiration(1)}>2小时</button>
            <button className="m-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleUpdateExpiration(2)}>4小时</button>
            <button className="m-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleUpdateExpiration(3)}>1天</button>
            <button className="m-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleUpdateExpiration(4)}>3天</button>
            <button className="m-1 px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600" onClick={() => handleUpdateExpiration(5)}>7天</button>
          </div>
        </div>
      )}

      <div className="mt-2">
        {message.type === 'code' ? (
          <CodeMirror
            value={message.content}
            height="auto"
            extensions={[javascript()]}
            editable={false}
          />
        ) : message.type === 'image' ? (
          <img
            src={message.file_path}
            alt="消息图片"
            className="max-h-[200px] w-auto object-contain rounded cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(message.file_path)}
          />
        ) : message.type === 'file' ? (
          <a
            href={message.file_path}
            download
            className="flex items-center space-x-2 p-2 bg-gray-50 rounded hover:bg-gray-100"
          >
            <DocumentIcon className="w-5 h-5 text-gray-500" />
            <span className="text-primary">{message.content}</span>
          </a>
        ) : (
          <div onDoubleClick={handleDoubleClick}>
            <p className="whitespace-pre-wrap">{displayContent}</p>
            {shouldCollapse && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 text-primary hover:underline flex items-center"
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="w-4 h-4 mr-1" />
                    收起
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-4 h-4 mr-1" />
                    展开
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
      {/* {isModalOpen && (
        <ExpirationModal
          onClose={() => setIsModalOpen(false)}
          onSelect={handleUpdateExpiration}
        />
      )} */}
    </div>
  );
};

export default MessageItem; 